import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import GoogleSSOButton from "@/components/GoogleSSOButton";
import { getPublicVideoUrl } from "@/lib/storageUrls";


export default function Landing() {
  const [, setLocation] = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isValuesModalOpen, setIsValuesModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isClientsModalOpen, setIsClientsModalOpen] = useState(false);
  const [isEthosModalOpen, setIsEthosModalOpen] = useState(false);
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [flippedCards, setFlippedCards] = useState<Record<string, boolean>>({});
  const [ethosVideoUrl, setEthosVideoUrl] = useState<string>('');
  const [galleryVideoUrl, setGalleryVideoUrl] = useState<string>('');

  const toggleFlip = (cardId: string) => {
    setFlippedCards(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
  };

  // Load video URLs dynamically from Firebase Storage (public videos, no auth required)
  useEffect(() => {
    const loadVideoUrls = async () => {
      try {
        const [ethosUrl, galleryUrl] = await Promise.all([
          getPublicVideoUrl('showcase-videos/ethos.mp4').catch((err) => {
            console.error('Failed to load Ethos video:', err);
            return '';
          }),
          getPublicVideoUrl('showcase-videos/Gallery.mp4').catch((err) => {
            console.error('Failed to load Gallery video:', err);
            return '';
          })
        ]);
        setEthosVideoUrl(ethosUrl);
        setGalleryVideoUrl(galleryUrl);
      } catch (error) {
        console.error('Failed to load video URLs:', error);
      }
    };
    loadVideoUrls();
  }, []);

  const speakWelcome = () => {
    if ('speechSynthesis' in window && !isSpeaking) {
      setIsSpeaking(true);
      
      const utterance = new SpeechSynthesisUtterance("Welcome to Servify LaunchPad!");
      
      // Function to find and set female voice
      const getVoices = () => {
        const voices = speechSynthesis.getVoices();
        console.log('Available voices:', voices.map(v => v.name));
        
        // Try to find a female voice
        const femaleVoice = voices.find(voice => 
          voice.lang.includes('en') && 
          (voice.name.includes('female') || 
           voice.name.includes('woman') || 
           voice.name.includes('girl') ||
           voice.name.includes('Samantha') ||
           voice.name.includes('Victoria') ||
           voice.name.includes('Karen'))
        );
        
        if (femaleVoice) {
          utterance.voice = femaleVoice;
          console.log('Using female voice:', femaleVoice.name);
        } else {
          console.log('No female voice found, using default');
        }
        
        // Set speech properties
        utterance.rate = 0.9;
        utterance.pitch = 1.1;
        utterance.volume = 0.8;
        
        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = () => setIsSpeaking(false);
        
        // Speak the message
        speechSynthesis.speak(utterance);
      };
      
      // Always set basic properties first
      utterance.rate = 0.9;
      utterance.pitch = 1.1;
      utterance.volume = 0.8;
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);
      
      // Try to get voices immediately
      let voices = speechSynthesis.getVoices();
      
      // If voices are available, try to find female voice
      if (voices.length > 0) {
        getVoices();
      } else {
        // No voices available yet, set up event handler and speak immediately with default
        speechSynthesis.onvoiceschanged = getVoices;
        // Speak immediately with default voice to ensure it works on first hover
        speechSynthesis.speak(utterance);
      }
    }
  };

  return (
    <>
      <div className="min-h-screen flex flex-col lg:flex-row bg-white font-muli">
      {/* Left Side - Text and Login Buttons */}
      <div className="flex-1 flex flex-col justify-center px-8 lg:px-20 py-8 lg:py-16">
        <div className="max-w-xl mx-auto lg:mx-0">
          <h1 className="text-4xl lg:text-6xl font-bold text-servify-purple leading-tight mb-6 text-center lg:text-left">
            Welcome to<br />
            <span className="text-servify-purple">Servify</span><br />
            <span className="text-servify-purple">LaunchPad</span>

          </h1>

          <p className="text-lg lg:text-xl text-servify-medium-grey mb-6 leading-relaxed font-medium text-center lg:text-left">
            Where every journey takes off.
            {/* AI Avatar Character - Inline
            <span className="inline-block ml-4 lg:ml-6 align-middle">
              <div className="relative group cursor-pointer inline-block" onClick={speakWelcome} onMouseEnter={speakWelcome}>

                <img 
                  src="/character-illustration.png" 
                  alt="AI Guide Character" 
                  className={`w-20 h-20 lg:w-28 lg:h-28 object-contain transition-all duration-300 hover:scale-110 ${isSpeaking ? 'animate-pulse' : 'animate-float'}`}
                />
              </div>
            </span> */}
          </p>

          <div className="space-y-4">
            <Button 
              onClick={() => setLocation("/login")}
              className="w-full bg-servify-alt-green hover:bg-servify-white-grey hover:text-servify-purple text-servify-purple text-base lg:text-lg font-semibold py-4 lg:py-5 px-6 lg:px-8 rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Login as an aspiring Servifyte
            </Button>
            
            <GoogleSSOButton 
              className="w-full bg-servify-purple hover:bg-servify-white-grey hover:text-servify-purple text-white text-base lg:text-lg font-semibold py-4 lg:py-5 px-6 lg:px-8 rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Login as a Servifyte
            </GoogleSSOButton>
          </div>
        </div>
      </div>

      {/* Right Side - Illustration and Floating Cards */}
              <div className="flex-1 flex items-center justify-center relative bg-white px-8 lg:px-20">
        {/* Background decorative elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-8 lg:top-16 left-8 lg:left-16 w-16 h-16 lg:w-24 lg:h-24 xl:w-32 xl:h-32 bg-servify-purple/10 rounded-full blur-lg lg:blur-xl"></div>
          <div className="absolute bottom-8 lg:bottom-16 right-8 lg:right-16 w-20 h-20 lg:w-28 lg:h-28 xl:w-40 xl:h-40 bg-servify-green/10 rounded-full blur-lg lg:blur-xl"></div>
          <div className="absolute top-1/2 left-1/4 lg:left-1/3 w-12 h-12 lg:w-16 lg:h-16 xl:w-24 xl:h-24 bg-servify-alt-green/10 rounded-full blur-lg"></div>
        </div>



        {/* 6 Flip Cards - Centered Grid Layout */}
        <div className="w-full max-w-5xl mx-auto flex items-center justify-center font-muli py-8 lg:py-12">
          <div className="grid grid-cols-3 gap-4 lg:gap-6 xl:gap-8">
            {/* Row 1: Green, Purple, Green */}
            {/* Card 1 - Our Ethos (Green) */}
            <div 
              className={`flip-card group cursor-pointer ${flippedCards['ethos'] ? 'flipped' : ''}`}
              onClick={() => toggleFlip('ethos')}
            >
              <div className="flip-card-inner">
                {/* Front Side */}
                <div className="flip-card-front bg-servify-alt-green rounded-2xl p-4 lg:p-6 shadow-xl flex flex-col items-center justify-center text-servify-purple">
                  <div className="w-12 h-12 lg:w-16 lg:h-16 bg-servify-purple/20 rounded-full flex items-center justify-center mb-3 lg:mb-4">
                    <img src="/assets/our-ethos.png" alt="Our Ethos" className="w-6 h-6 lg:w-8 lg:h-8 object-contain" />
                  </div>
                  <h3 className="text-base lg:text-lg font-bold">Our Ethos</h3>
                  {/* <p className="text-xs lg:text-sm text-servify-dark-grey mt-1 lg:mt-2">Hover to learn more</p> */}
                </div>
                
                {/* Back Side */}
                <div className="flip-card-back bg-white rounded-2xl p-4 lg:p-6 shadow-xl flex flex-col items-center justify-center text-servify-dark-grey">
                  <h3 className="text-base lg:text-lg font-bold text-servify-alt-green mb-2 lg:mb-3">Our Ethos</h3>
                  <p className="text-xs lg:text-sm text-center leading-relaxed">
                    Excellence & integrity in everything we do.
                  </p>
                  <button 
                    className="mt-2 lg:mt-3 px-3 py-1.5 lg:px-4 lg:py-2 bg-servify-alt-green text-white rounded-lg text-xs lg:text-sm hover:bg-servify-green transition-colors"
                    onClick={(e) => { e.stopPropagation(); setIsEthosModalOpen(true); }}
                  >
                    Explore Ethos
                  </button>
                </div>
              </div>
            </div>

            {/* Card 2 - Our Team */}
            <div 
              className={`flip-card group cursor-pointer ${flippedCards['team'] ? 'flipped' : ''}`}
              onClick={() => toggleFlip('team')}
            >
              <div className="flip-card-inner">
                {/* Front Side */}
                <div className="flip-card-front bg-servify-purple rounded-2xl p-4 lg:p-6 shadow-xl flex flex-col items-center justify-center text-white">
                  <div className="w-12 h-12 lg:w-16 lg:h-16 bg-white/20 rounded-full flex items-center justify-center mb-3 lg:mb-4">
                    <img src="/assets/our-team.png" alt="Our Team" className="w-6 h-6 lg:w-8 lg:h-8 object-contain" />
                  </div>
                  <h3 className="text-base lg:text-lg font-bold">Our Team</h3>
                  {/* <p className="text-xs lg:text-sm text-servify-light-grey mt-1 lg:mt-2">Hover to learn more</p> */}
                </div>
                
                {/* Back Side */}
                <div className="flip-card-back bg-white rounded-2xl p-4 lg:p-6 shadow-xl flex flex-col items-center justify-center text-servify-dark-grey">
                  <h3 className="text-base lg:text-lg font-bold text-servify-purple mb-2 lg:mb-3">Our Team</h3>
                  <p className="text-xs lg:text-sm text-center leading-relaxed">
                    Passionate professionals transforming the industry.
                  </p>
                  <button 
                    className="mt-2 lg:mt-3 px-3 py-1.5 lg:px-4 lg:py-2 bg-servify-purple text-white rounded-lg text-xs lg:text-sm hover:bg-servify-dark-grey transition-colors"
                    onClick={(e) => { e.stopPropagation(); setIsModalOpen(true); }}
                  >
                    Explore Team
                  </button>
                </div>
              </div>
            </div>

            {/* Card 3 - Values */}
            <div 
              className={`flip-card group cursor-pointer ${flippedCards['values'] ? 'flipped' : ''}`}
              onClick={() => toggleFlip('values')}
            >
              <div className="flip-card-inner">
                {/* Front Side */}
                <div className="flip-card-front bg-servify-alt-green rounded-2xl p-4 lg:p-6 shadow-xl flex flex-col items-center justify-center text-servify-purple">
                  <div className="w-12 h-12 lg:w-16 lg:h-16 bg-servify-purple/20 rounded-full flex items-center justify-center mb-3 lg:mb-4">
                    <img src="/assets/our-values.png" alt="Our Values" className="w-6 h-6 lg:w-8 lg:h-8 object-contain" />
                  </div>
                  <h3 className="text-base lg:text-lg font-bold">Our Values</h3>
                  {/* <p className="text-xs lg:text-sm text-servify-dark-grey mt-1 lg:mt-2">Hover to learn more</p> */}
                </div>
                
                {/* Back Side */}
                <div className="flip-card-back bg-white rounded-2xl p-4 lg:p-6 shadow-xl flex flex-col items-center justify-center text-servify-dark-grey">
                  <h3 className="text-base lg:text-lg font-bold text-servify-alt-green mb-2 lg:mb-3">Values</h3>
                  <p className="text-xs lg:text-sm text-center leading-relaxed">
                    Our core values drive everything we do.
                  </p>
                  <button 
                    className="mt-2 lg:mt-3 px-3 py-1.5 lg:px-4 lg:py-2 bg-servify-alt-green text-white rounded-lg text-xs lg:text-sm hover:bg-servify-green transition-colors"
                    onClick={(e) => { e.stopPropagation(); setIsValuesModalOpen(true); }}
                  >
                    Explore Values
                  </button>
                </div>
              </div>
            </div>

            {/* Row 2: Purple, Green, Purple */}
            {/* Card 4 - History (Purple) */}
            <div 
              className={`flip-card group cursor-pointer ${flippedCards['history'] ? 'flipped' : ''}`}
              onClick={() => toggleFlip('history')}
            >
              <div className="flip-card-inner">
                {/* Front Side */}
                <div className="flip-card-front bg-servify-purple rounded-2xl p-4 lg:p-6 shadow-xl flex flex-col items-center justify-center text-white">
                  <div className="w-12 h-12 lg:w-16 lg:h-16 bg-white/20 rounded-full flex items-center justify-center mb-3 lg:mb-4">
                    <img src="/assets/history.png" alt="Our History" className="w-6 h-6 lg:w-8 lg:h-8 object-contain" />
                  </div>
                  <h3 className="text-base lg:text-lg font-bold">History</h3>
                  {/* <p className="text-xs lg:text-sm text-servify-light-grey mt-1 lg:mt-2">Hover to learn more</p> */}
                </div>
                
                                {/* Back Side */}
                <div className="flip-card-back bg-white rounded-2xl p-4 lg:p-6 shadow-xl flex flex-col items-center justify-center text-servify-dark-grey">
                  <h3 className="text-base lg:text-lg font-bold text-servify-purple mb-2 lg:mb-3">History</h3>
                  <p className="text-xs lg:text-sm text-center leading-relaxed">
                   From modest origins to shaping global partnerships.
                  </p>
                  <button 
                    className="mt-2 lg:mt-3 px-3 py-1.5 lg:px-4 lg:py-2 bg-servify-purple text-white rounded-lg text-xs lg:text-sm hover:bg-servify-dark-grey transition-colors"
                    onClick={(e) => { e.stopPropagation(); setIsHistoryModalOpen(true); }}
                  >
                    Explore History
                  </button>
                </div>
              </div>
            </div>

            {/* Card 5 - Innovation */}
            <div 
              className={`flip-card group cursor-pointer ${flippedCards['partners'] ? 'flipped' : ''}`}
              onClick={() => toggleFlip('partners')}
            >
              <div className="flip-card-inner">
                {/* Front Side */}
                <div className="flip-card-front bg-servify-alt-green rounded-2xl p-4 lg:p-6 shadow-xl flex flex-col items-center justify-center text-servify-purple">
                  <div className="w-12 h-12 lg:w-16 lg:h-16 bg-servify-purple/20 rounded-full flex items-center justify-center mb-3 lg:mb-4">
                    <img src="/assets/our-partners.png" alt="Our Partners" className="w-6 h-6 lg:w-8 lg:h-8 object-contain" />
                  </div>
                  <h3 className="text-base lg:text-lg font-bold">Our Partners</h3>
                  {/* <p className="text-xs lg:text-sm text-servify-dark-grey mt-1 lg:mt-2">Hover to learn more</p> */}
                </div>
                
                                {/* Back Side */}
                <div className="flip-card-back bg-white rounded-2xl p-4 lg:p-6 shadow-xl flex flex-col items-center justify-center text-servify-dark-grey">
                  <h3 className="text-base lg:text-lg font-bold text-servify-alt-green mb-2 lg:mb-3">Our Partners</h3>
                  <p className="text-xs lg:text-sm text-center leading-relaxed">
                   Driving growth for industry leaders.
                  </p>
                  <button 
                    className="mt-2 lg:mt-3 px-3 py-1.5 lg:px-4 lg:py-2 bg-servify-alt-green text-white rounded-lg text-xs lg:text-sm hover:bg-servify-green transition-colors"
                    onClick={(e) => { e.stopPropagation(); setIsClientsModalOpen(true); }}
                  >
                    Explore Partners
                  </button>
                </div>
              </div>
            </div>

            {/* Card 6 - Gallery */}
            <div 
              className={`flip-card group cursor-pointer ${flippedCards['gallery'] ? 'flipped' : ''}`}
              onClick={() => toggleFlip('gallery')}
            >
              <div className="flip-card-inner">
                {/* Front Side */}
                <div className="flip-card-front bg-servify-purple rounded-2xl p-4 lg:p-6 shadow-xl flex flex-col items-center justify-center text-white">
                  <div className="w-12 h-12 lg:w-16 lg:h-16 bg-white/20 rounded-full flex items-center justify-center mb-3 lg:mb-4">
                    <img src="/assets/gallery.png" alt="Gallery" className="w-6 h-6 lg:w-8 lg:h-8 object-contain" />
                  </div>
                  <h3 className="text-base lg:text-lg font-bold">Gallery</h3>
                  {/* <p className="text-xs lg:text-sm text-servify-light-grey mt-1 lg:mt-2">Hover to le
                  arn more</p> */}
                </div>
                
                                {/* Back Side */}
                <div className="flip-card-back bg-white rounded-2xl p-4 lg:p-6 shadow-xl flex flex-col items-center justify-center text-servify-dark-grey">
                  <h3 className="text-base lg:text-lg font-bold text-servify-purple mb-2 lg:mb-3">Gallery</h3>
                  <p className="text-xs lg:text-sm text-center leading-relaxed">
                   A montage of life at Servify.
                  </p>
                  <button 
                    className="mt-2 lg:mt-3 px-3 py-1.5 lg:px-4 lg:py-2 bg-servify-purple text-white rounded-lg text-xs lg:text-sm hover:bg-servify-dark-grey transition-colors"
                    onClick={(e) => { e.stopPropagation(); setIsGalleryModalOpen(true); }}
                  >
                    Explore Gallery
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>



      {/* Values Modal */}
      {isValuesModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 font-muli">
          <div className="bg-white rounded-2xl lg:rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative animate-in fade-in-0 zoom-in-95 duration-300">
            {/* Modal Header */}
            <div className="bg-servify-alt-green text-servify-purple p-4 lg:p-6 relative">
              <h2 className="text-2xl lg:text-3xl font-bold text-center">Our Values</h2>
              <p className="text-servify-purple/80 text-center mt-2 text-sm lg:text-base">The principles that guide everything we do</p>
              
              {/* Close Button */}
              <button
                onClick={() => setIsValuesModalOpen(false)}
                className="absolute top-3 lg:top-4 right-3 lg:right-4 w-8 h-8 lg:w-10 lg:h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
              >
                <svg className="w-5 h-5 lg:w-6 lg:h-6 text-servify-purple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 lg:p-8 flex flex-col items-center">
              <div className="relative group">
                <img 
                  src="/core_values_highres.png" 
                  alt="Our Core Values" 
                  className="w-full h-auto max-w-2xl rounded-2xl shadow-xl transition-transform duration-300 group-hover:scale-105"
                />
                {/* Subtle overlay effect */}
                <div className="absolute inset-0 bg-servify-dark-grey/5 rounded-2xl pointer-events-none"></div>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute top-4 left-4 w-3 h-3 bg-servify-purple rounded-full animate-pulse"></div>
            <div className="absolute bottom-4 right-4 w-4 h-4 bg-servify-alt-green rounded-full animate-pulse delay-100"></div>
          </div>
        </div>
      )}

      {/* Team Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 font-muli">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative">
            {/* Modal Header */}
            <div className="bg-servify-purple text-white p-4 lg:p-6 relative">
              <h2 className="text-2xl lg:text-3xl font-bold text-center">Our Team</h2>
              <p className="text-purple-100 text-center mt-2 text-sm lg:text-base">Meet the people behind Servify</p>
              
              {/* Close Button */}
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-3 lg:top-4 right-3 lg:right-4 w-8 h-8 lg:w-10 lg:h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
              >
                <svg className="w-5 h-5 lg:w-6 lg:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 lg:p-8 flex flex-col items-center">
              <div className="relative group">
                <img 
                  src="/image (1).png" 
                  alt="Our Team" 
                  className="w-full h-auto max-w-2xl rounded-2xl shadow-xl transition-transform duration-300 group-hover:scale-105"
                />
                {/* Subtle overlay effect */}
                <div className="absolute inset-0 bg-servify-dark-grey/5 rounded-2xl pointer-events-none"></div>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute top-4 left-4 w-3 h-3 bg-servify-green rounded-full animate-pulse"></div>
            <div className="absolute bottom-4 right-4 w-4 h-4 bg-servify-purple rounded-full animate-pulse delay-100"></div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {isHistoryModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 font-muli">
          <div className="bg-white rounded-2xl lg:rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative animate-in fade-in-0 zoom-in-95 duration-300">
            {/* Modal Header */}
            <div className="bg-servify-purple text-white p-4 lg:p-6 relative">
              <h2 className="text-2xl lg:text-3xl font-bold text-center">Our History</h2>
              <p className="text-white/80 text-center mt-2 text-sm lg:text-base">Discover our journey from humble beginnings to global leadership</p>
              
              {/* Close Button */}
              <button
                onClick={() => setIsHistoryModalOpen(false)}
                className="absolute top-3 lg:top-4 right-3 lg:right-4 w-8 h-8 lg:w-10 lg:h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
              >
                <svg className="w-5 h-5 lg:w-6 lg:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 lg:p-8 flex flex-col items-center">
              <div className="relative group">
                <img 
                  src="/history.png" 
                  alt="Our History" 
                  className="w-full h-auto max-w-2xl rounded-2xl shadow-xl transition-transform duration-300 group-hover:scale-105"
                />
                {/* Subtle overlay effect */}
                <div className="absolute inset-0 bg-servify-dark-grey/5 rounded-2xl pointer-events-none"></div>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute top-4 left-4 w-3 h-3 bg-servify-alt-green rounded-full animate-pulse"></div>
            <div className="absolute bottom-4 right-4 w-4 h-4 bg-servify-purple rounded-full animate-pulse delay-100"></div>
          </div>
        </div>
      )}

      {/* Ethos Video Modal */}
      {isEthosModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 font-muli">
          <div className="bg-white rounded-2xl lg:rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative animate-in fade-in-0 zoom-in-95 duration-300">
            {/* Modal Header */}
            <div className="bg-servify-alt-green text-servify-purple p-4 lg:p-6 relative">
              <h2 className="text-2xl lg:text-3xl font-bold text-center">Our Ethos</h2>
              <p className="text-servify-purple/80 text-center mt-2 text-sm lg:text-base">Excellence & integrity in everything we do</p>
              
              {/* Close Button */}
              <button
                onClick={() => setIsEthosModalOpen(false)}
                className="absolute top-3 lg:top-4 right-3 lg:right-4 w-8 h-8 lg:w-10 lg:h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
              >
                <svg className="w-5 h-5 lg:w-6 lg:h-6 text-servify-purple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 lg:p-8 flex flex-col items-center">
              <div className="relative group w-full max-w-2xl">
                {ethosVideoUrl ? (
                  <video 
                    src={ethosVideoUrl}
                    controls
                    className="w-full h-auto rounded-2xl shadow-xl"
                    autoPlay
                    muted
                    onError={(e) => {
                      console.error('Ethos video failed to load');
                      const video = e.target as HTMLVideoElement;
                      const container = video.parentElement;
                      if (container) {
                        container.innerHTML = `
                          <div class="w-full h-64 bg-gray-100 flex flex-col items-center justify-center rounded-2xl">
                            <svg class="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                            <span class="text-gray-600 font-medium">Video unavailable</span>
                            <span class="text-gray-500 text-sm mt-1">The Ethos video is currently unavailable. Please ensure the file is uploaded to Firebase Storage at showcase-videos/ethos.mp4</span>
                          </div>
                        `;
                      }
                    }}
                  >
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div className="w-full h-64 bg-gray-100 flex flex-col items-center justify-center rounded-2xl">
                    <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <span className="text-gray-600 font-medium">Loading video...</span>
                  </div>
                )}
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute top-4 left-4 w-3 h-3 bg-servify-purple rounded-full animate-pulse"></div>
            <div className="absolute bottom-4 right-4 w-4 h-4 bg-servify-alt-green rounded-full animate-pulse delay-100"></div>
          </div>
        </div>
      )}

      {/* Gallery Video Modal */}
      {isGalleryModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 font-muli">
          <div className="bg-white rounded-2xl lg:rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative animate-in fade-in-0 zoom-in-95 duration-300">
            {/* Modal Header */}
            <div className="bg-servify-purple text-white p-4 lg:p-6 relative">
              <h2 className="text-2xl lg:text-3xl font-bold text-center">Gallery</h2>
              <p className="text-purple-100 text-center mt-2 text-sm lg:text-base">A montage of life at Servify</p>
              
              {/* Close Button */}
              <button
                onClick={() => setIsGalleryModalOpen(false)}
                className="absolute top-3 lg:top-4 right-3 lg:right-4 w-8 h-8 lg:w-10 lg:h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
              >
                <svg className="w-5 h-5 lg:w-6 lg:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 lg:p-8 flex flex-col items-center">
              <div className="relative group w-full max-w-2xl">
                {galleryVideoUrl ? (
                  <video 
                    src={galleryVideoUrl}
                    controls
                    className="w-full h-auto rounded-2xl shadow-xl"
                    autoPlay
                    muted
                    onError={(e) => {
                      console.error('Gallery video failed to load');
                      const video = e.target as HTMLVideoElement;
                      const container = video.parentElement;
                      if (container) {
                        container.innerHTML = `
                          <div class="w-full h-64 bg-gray-100 flex flex-col items-center justify-center rounded-2xl">
                            <svg class="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                            <span class="text-gray-600 font-medium">Video unavailable</span>
                            <span class="text-gray-500 text-sm mt-1">The Gallery video is currently unavailable. Please ensure the file is uploaded to Firebase Storage at showcase-videos/Gallery.mp4</span>
                          </div>
                        `;
                      }
                    }}
                  >
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div className="w-full h-64 bg-gray-100 flex flex-col items-center justify-center rounded-2xl">
                    <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <span className="text-gray-600 font-medium">Loading video...</span>
                  </div>
                )}
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute top-4 left-4 w-3 h-3 bg-servify-purple rounded-full animate-pulse"></div>
            <div className="absolute bottom-4 right-4 w-4 h-4 bg-servify-green rounded-full animate-pulse delay-100"></div>
          </div>
        </div>
      )}

      {/* Clients Modal */}
      {isClientsModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 font-muli">
          <div className="bg-white rounded-2xl lg:rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative animate-in fade-in-0 zoom-in-95 duration-300">
            {/* Modal Header */}
            <div className="bg-servify-green text-white p-4 lg:p-6 relative">
              <h2 className="text-2xl lg:text-3xl font-bold text-center">Our Partners</h2>
              <p className="text-green-100 text-center mt-2 text-sm lg:text-base">Industry leaders who trust us</p>
              
              {/* Close Button */}
              <button
                onClick={() => setIsClientsModalOpen(false)}
                className="absolute top-3 lg:top-4 right-3 lg:right-4 w-8 h-8 lg:w-10 lg:h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
              >
                <svg className="w-5 h-5 lg:w-6 lg:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 lg:p-8 flex flex-col items-center">
              <div className="relative group">
                <img 
                  src="/clients.png" 
                  alt="Our Partners" 
                  className="w-full h-auto max-w-2xl rounded-2xl shadow-xl transition-transform duration-300 group-hover:scale-105"
                />
                {/* Subtle overlay effect */}
                <div className="absolute inset-0 bg-servify-dark-grey/5 rounded-2xl pointer-events-none"></div>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute top-4 left-4 w-3 h-3 bg-servify-purple rounded-full animate-pulse"></div>
            <div className="absolute bottom-4 right-4 w-4 h-4 bg-servify-green rounded-full animate-pulse delay-100"></div>
          </div>
        </div>
      )}
    </>
  );
}
